import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import {
  adminLogPageSize,
  adminUserPageSize,
  type AdminAiAbuseReview,
  type AdminAppLog,
  type AdminCreditAdjustmentInput,
  type AdminCreditAdjustmentResult,
  type AdminDeleteAccountInput,
  type AdminDeleteAccountResult,
  type AdminLogsFilters,
  type AdminLogsPage,
  type AdminPqrListFilters,
  type AdminPqrPage,
  type AdminTierUpdateInput,
  type AdminTierUpdateResult,
  type AdminUserDetail,
  type AdminUserDirectoryPage,
  type AdminUsersFilters,
  type AcknowledgeAppLogResult,
  type BanUserInput,
  type BanUserResult,
  type CreateTestAccountInput,
  type CreateTestAccountResult,
  type UnbanUserInput,
  type UnbanUserResult,
} from "@/features/admin/admin.types";
import {
  mapAdminAiAbuseReview,
  mapAdminAppLog,
  mapAdminDeleteAccountResult,
  mapAdminLogsPage,
  mapAdminPqrListItem,
  mapAdminTierUpdateResult,
  mapAdminUserDetail,
  mapAdminUsersPage,
  mapAcknowledgeAppLogResult,
  mapBanUserResult,
  mapCreateTestAccountResult,
  mapUnbanUserResult,
} from "@/features/admin/adminMappers";

export class AdminClientError extends Error {
  constructor(
    public readonly code: string,
    message?: string,
    public readonly cause?: unknown,
  ) {
    super(message ?? code);
    this.name = "AdminClientError";
  }
}

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new AdminClientError("missing-config", "Supabase environment variables are not configured.");
  }
}

function normalizeFunctionError(error: unknown, fallbackCode: string) {
  if (!error || typeof error !== "object") {
    return new AdminClientError(fallbackCode);
  }

  const candidate = error as {
    code?: string;
    context?: {
      json?: {
        code?: string;
        message?: string;
      };
    };
    message?: string;
  };

  const bodyCode = candidate.context?.json?.code;
  const bodyMessage = candidate.context?.json?.message;
  return new AdminClientError(bodyCode ?? fallbackCode, bodyMessage ?? candidate.message, error);
}

export async function loadAdminUsersPage(filters: AdminUsersFilters): Promise<AdminUserDirectoryPage> {
  ensureConfigured();

  const { data, error } = await supabase.rpc("list_admin_users", {
    page_number: filters.page,
    page_size: adminUserPageSize,
    search_text: filters.searchText.trim() || undefined,
    status_filter: filters.statusFilter === "all" ? undefined : filters.statusFilter,
    tier_filter: filters.tierFilter === "all" ? undefined : filters.tierFilter,
  });

  if (error) {
    throw new AdminClientError(error.message, error.message, error);
  }

  return mapAdminUsersPage(data);
}

export async function loadAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  ensureConfigured();

  const { data, error } = await supabase.rpc("get_admin_user_detail", {
    target_user_id: userId,
  });

  if (error) {
    throw new AdminClientError(error.message, error.message, error);
  }

  return mapAdminUserDetail(data);
}

export async function loadAdminUserAiAbuseReviews(userId: string): Promise<AdminAiAbuseReview[]> {
  ensureConfigured();

  const client = supabase as unknown as {
    from: (table: string) => ReturnType<typeof supabase.from>;
  };

  const { data, error } = await client
    .from("ai_abuse_reviews")
    .select("*")
    .eq("user_id", userId)
    .order("detected_at", { ascending: false })
    .limit(25);

  if (error) {
    throw new AdminClientError(error.message, error.message, error);
  }

  return (data ?? []).map((entry: unknown) => mapAdminAiAbuseReview(entry));
}

export async function createTestAccount(
  input: CreateTestAccountInput,
): Promise<CreateTestAccountResult> {
  ensureConfigured();

  const { data, error } = await supabase.functions.invoke("admin-create-user", {
    body: input,
  });

  if (error) {
    throw normalizeFunctionError(error, "ADMIN_CREATE_USER_FAILED");
  }

  return mapCreateTestAccountResult(data);
}

export async function updateUserTier(
  input: AdminTierUpdateInput,
): Promise<AdminTierUpdateResult> {
  ensureConfigured();
  const userResult = await supabase.auth.getUser();
  if (userResult.error || !userResult.data.user) {
    throw new AdminClientError(
      "ADMIN_FORBIDDEN",
      userResult.error?.message ?? "No authenticated admin user available.",
      userResult.error,
    );
  }

  const { data, error } = await supabase.rpc("admin_update_user_tier", {
    admin_user_id: userResult.data.user.id,
    next_tier: input.tier,
    reason: input.reason,
    target_user_id: input.userId,
  });

  if (error) {
    throw new AdminClientError(error.message, error.message, error);
  }

  return mapAdminTierUpdateResult(data);
}

async function invokeCreditFunction(
  name: "admin-grant-credits" | "admin-deduct-credits",
  input: AdminCreditAdjustmentInput,
): Promise<AdminCreditAdjustmentResult> {
  ensureConfigured();

  const { data, error } = await supabase.functions.invoke(name, {
    body: input,
  });

  if (error || !data || typeof data !== "object") {
    throw normalizeFunctionError(error ?? data, "ADMIN_CREDIT_ADJUST_FAILED");
  }

  const value = data as {
    feature?: AdminCreditAdjustmentResult["feature"];
    newBalance?: number;
    userId?: string;
  };

  return {
    feature: value.feature === "admin_deduct" ? "admin_deduct" : "admin_grant",
    newBalance: typeof value.newBalance === "number" ? value.newBalance : 0,
    userId: typeof value.userId === "string" ? value.userId : input.userId,
  };
}

export function grantCredits(input: AdminCreditAdjustmentInput) {
  return invokeCreditFunction("admin-grant-credits", input);
}

export function deductCredits(input: AdminCreditAdjustmentInput) {
  return invokeCreditFunction("admin-deduct-credits", input);
}

export async function banUser(input: BanUserInput): Promise<BanUserResult> {
  ensureConfigured();

  const { data, error } = await supabase.functions.invoke("admin-ban-user", {
    body: input,
  });

  if (error) {
    throw normalizeFunctionError(error, "ADMIN_BAN_FAILED");
  }

  return mapBanUserResult(data);
}

export async function unbanUser(input: UnbanUserInput): Promise<UnbanUserResult> {
  ensureConfigured();

  const { data, error } = await supabase.functions.invoke("admin-unban-user", {
    body: input,
  });

  if (error) {
    throw normalizeFunctionError(error, "ADMIN_BAN_FAILED");
  }

  return mapUnbanUserResult(data);
}

export async function deleteAccountImmediately(
  input: AdminDeleteAccountInput,
): Promise<AdminDeleteAccountResult> {
  ensureConfigured();

  const { data, error } = await supabase.functions.invoke("admin-delete-account", {
    body: input,
  });

  if (error) {
    throw normalizeFunctionError(error, "ADMIN_BAN_FAILED");
  }

  return mapAdminDeleteAccountResult(data);
}

export async function loadAdminPqrPage(filters: AdminPqrListFilters): Promise<AdminPqrPage> {
  ensureConfigured();

  const requestedPage = Math.max(filters.page, 1);
  const rangeFrom = (requestedPage - 1) * 20;
  const rangeTo = rangeFrom + 19;
  let query = supabase
    .from("pqr_submissions")
    .select("id, ticket_ref, name, email, category, subject, status, created_at, user_id", {
      count: "exact",
    })
    .order("created_at", { ascending: false });

  const searchText = filters.searchText.trim();
  if (searchText) {
    query = query.or(
      `ticket_ref.ilike.%${searchText}%,email.ilike.%${searchText}%,subject.ilike.%${searchText}%`,
    );
  }

  if (filters.status !== "all") {
    query = query.eq("status", filters.status);
  }

  if (filters.category !== "all") {
    query = query.eq("category", filters.category);
  }

  const result = await query.range(rangeFrom, rangeTo);
  if (result.error) {
    throw new AdminClientError(result.error.message, result.error.message, result.error);
  }

  const totalCount = Math.max(0, result.count ?? 0);
  return {
    items: (result.data ?? []).map((item) => mapAdminPqrListItem(item)),
    page: requestedPage,
    pageCount: Math.max(1, Math.ceil(totalCount / 20)),
    totalCount,
  };
}

export async function updateAdminPqrStatus(input: {
  nextStatus: string;
  note?: string;
  requestId: string;
}) {
  ensureConfigured();

  const { data, error } = await supabase.rpc("admin_update_pqr_status", {
    next_status: input.nextStatus,
    note: input.note ?? undefined,
    target_submission_id: input.requestId,
  });

  if (error) {
    throw new AdminClientError(error.message, error.message, error);
  }

  return data;
}

export async function loadAdminLogsPage(filters: AdminLogsFilters): Promise<AdminLogsPage> {
  ensureConfigured();

  const requestedPage = Math.max(filters.page, 1);
  const rangeFrom = (requestedPage - 1) * adminLogPageSize;
  const rangeTo = rangeFrom + adminLogPageSize - 1;
  let query = supabase
    .from("app_logs")
    .select("*", { count: "exact" })
    .order("created_at", { ascending: false });

  const searchText = filters.searchText.trim();
  if (searchText) {
    query = query.or(`message.ilike.%${searchText}%,source.ilike.%${searchText}%`);
  }

  if (filters.severity !== "all") {
    query = query.eq("severity", filters.severity);
  }

  if (filters.source.trim()) {
    query = query.ilike("source", `%${filters.source.trim()}%`);
  }

  if (filters.startDate) {
    query = query.gte("created_at", `${filters.startDate}T00:00:00.000Z`);
  }

  if (filters.endDate) {
    query = query.lte("created_at", `${filters.endDate}T23:59:59.999Z`);
  }

  const result = await query.range(rangeFrom, rangeTo);
  if (result.error) {
    throw new AdminClientError(result.error.message, result.error.message, result.error);
  }

  return mapAdminLogsPage({
    count: result.count,
    data: result.data ?? [],
    page: requestedPage,
    pageSize: adminLogPageSize,
  });
}

export async function acknowledgeAppLog(logId: string): Promise<AcknowledgeAppLogResult> {
  ensureConfigured();

  const { data, error } = await supabase.rpc("acknowledge_app_log", {
    target_log_id: logId,
  });

  if (error) {
    throw new AdminClientError(error.message, error.message, error);
  }

  return mapAcknowledgeAppLogResult(data);
}

export function mapAdminLogRows(rows: AdminAppLog[]) {
  return rows.map((row) => mapAdminAppLog(row));
}
